import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getMatchingCategory } from "firebase_/firebase_function/matching";
import { REGION_OPTIONS, parseRegionCsv } from "util/constants/regions";

const C = {
  greenDark: "#047857",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenBg: "#ECFDF5",
};

const MatchingFilter = ({ address, userRegion }) => {
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [regionOpen, setRegionOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const regionRef = useRef(null);

  const selected = searchParams.get("category") || null;
  // 다중 region 지원. URL ?region=대전,부산 같은 CSV. 비어있으면 마이페이지 region 단일값 폴백.
  const selectedRegions = parseRegionCsv(
    searchParams.get("region") ?? (userRegion && userRegion !== "전국" ? userRegion : ""),
  );
  const selectedRegionLabel =
    selectedRegions.length === 0
      ? "전국"
      : selectedRegions.length === 1
      ? selectedRegions[0]
      : `${selectedRegions[0]} 외 ${selectedRegions.length - 1}곳`;

  useEffect(() => {
    (async () => {
      try {
        const data = await getMatchingCategory();
        setCategories(Array.isArray(data) ? data : []);
      } catch {
        /* noop */
      }
    })();
  }, []);

  // 외부 클릭 시 닫기
  useEffect(() => {
    const onClick = (e) => {
      if (open && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
      if (regionOpen && regionRef.current && !regionRef.current.contains(e.target)) {
        setRegionOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, regionOpen]);

  const toggleRegion = (region) => {
    const next = new URLSearchParams(searchParams);
    // "전국" 클릭 → 모두 해제
    if (!region || region === "전국") {
      next.delete("region");
    } else {
      const set = new Set(selectedRegions);
      if (set.has(region)) set.delete(region);
      else set.add(region);
      if (set.size === 0) next.delete("region");
      else next.set("region", Array.from(set).join(","));
    }
    setSearchParams(next);
    sessionStorage.setItem(`page_${location.pathname}`, "1");
    // 드롭다운 유지 — 여러 개 연속 토글 가능
  };

  const options = useMemo(() => {
    return [
      { key: null, label: "전체" },
      ...categories.map((c) => ({ key: c.tag, label: c.tag })),
    ];
  }, [categories]);

  const selectCategory = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set("category", key);
    else next.delete("category");
    setSearchParams(next);
    sessionStorage.setItem(`page_${location.pathname}`, "1");
    sessionStorage.setItem(`pageIndex_${location.pathname}`, "1");
    setOpen(false);
    window.location.reload();
  };

  const selectedLabel = selected || "전체";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      {/* 지역 선택 */}
      <div ref={regionRef} style={{ position: "relative", userSelect: "none" }}>
        <button
          type="button"
          onClick={() => setRegionOpen((p) => !p)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            borderRadius: 10,
            background: selectedRegions.length === 0 ? "#fff" : C.greenBg,
            border: `1px solid ${selectedRegions.length === 0 ? C.line : C.greenDark}`,
            fontSize: 13,
            fontWeight: 700,
            color: selectedRegions.length === 0 ? C.body : C.greenDark,
            cursor: "pointer",
            minWidth: 160,
            justifyContent: "space-between",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden>📍</span>
            <span style={{ fontSize: 11, color: C.sub, fontWeight: 500 }}>지역</span>
            <span style={{ fontWeight: 800 }}>{selectedRegionLabel}</span>
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              fontSize: 11,
              color: C.sub,
              transition: "transform 0.15s ease",
              transform: regionOpen ? "rotate(180deg)" : "rotate(0)",
            }}
          />
        </button>

        {regionOpen && (
          <div
            role="listbox"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              minWidth: 200,
              maxHeight: 360,
              overflowY: "auto",
              background: "#fff",
              border: `1px solid ${C.line}`,
              borderRadius: 12,
              boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
              zIndex: 100,
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <div
              style={{
                padding: "8px 12px 6px",
                fontSize: 11,
                color: C.sub,
                fontWeight: 600,
                borderBottom: `1px solid ${C.line}`,
                marginBottom: 4,
              }}
            >
              여러 개 선택 가능 · 전국 클릭 시 전체 해제
            </div>
            {REGION_OPTIONS.map((r) => {
              const active =
                r === "전국"
                  ? selectedRegions.length === 0
                  : selectedRegions.includes(r);
              return (
                <button
                  key={r}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => toggleRegion(r)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: 8,
                    background: active ? C.greenBg : "transparent",
                    color: active ? C.greenDark : C.body,
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#F9FAFB";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span>{r}</span>
                  {active && <span style={{ color: C.greenDark, fontSize: 12 }}>✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div ref={dropdownRef} style={{ position: "relative", userSelect: "none" }}>
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 14px",
            borderRadius: 10,
            background: "#fff",
            border: `1px solid ${C.line}`,
            fontSize: 13,
            fontWeight: 600,
            color: C.body,
            cursor: "pointer",
            minWidth: 160,
            justifyContent: "space-between",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: C.sub, fontWeight: 500 }}>카테고리</span>
            <span style={{ color: C.ink, fontWeight: 700 }}>{selectedLabel}</span>
          </span>
          <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 11, color: C.sub, transition: "transform 0.15s ease", transform: open ? "rotate(180deg)" : "rotate(0)" }} />
        </button>

        {open && (
          <div
            role="listbox"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              minWidth: 200,
              maxHeight: 320,
              overflowY: "auto",
              background: "#fff",
              border: `1px solid ${C.line}`,
              borderRadius: 12,
              boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
              zIndex: 100,
              padding: 6,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {options.map((opt) => {
              const active = (opt.key ?? null) === selected;
              return (
                <button
                  key={opt.key ?? "_all"}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => selectCategory(opt.key)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: 8,
                    background: active ? C.greenBg : "transparent",
                    color: active ? C.greenDark : C.body,
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                    transition: "background 0.1s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#F9FAFB";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span>{opt.label}</span>
                  {active && (
                    <span aria-hidden style={{ color: C.greenDark, fontSize: 12 }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchingFilter;
